import { userRoleMapping, UserRole } from "constants/userRole";
import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useGetAllAccountsQuery } from "store/queries/account";
import { IAccount } from "types/Account/Account";

export function AccountIds() {
  const [filteredAccountIds, setFilteredAccountIds] = useState<IAccount[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedDefaultAccount, setSelectedDefaultAccount] = useState<
    string | undefined
  >();

  const { data, isFetching } = useGetAllAccountsQuery();
  const { setValue, watch } = useFormContext();

  const accounts = data?.data || [];

  const formAccountIds = watch("accountIds");
  const formDefaultAccount = watch("defaultAccount");
  const formScope = watch("scope");

  useEffect(() => {
    if (formAccountIds?.length && !isFetching) {
      const accountIdsAlreadySelected = accounts.filter((account) =>
        formAccountIds.includes(account.id)
      );
      setSelectedAccountIds(
        accountIdsAlreadySelected.map(
          (account: IAccount) => `${account.id} - ${account.name}`
        )
      );
    }
  }, [formAccountIds, isFetching]);

  useEffect(() => {
    if (!isFetching && formDefaultAccount) {
      const defaultAccount = accounts.find(
        (account) => account.id === formDefaultAccount
      );
      setSelectedDefaultAccount(
        defaultAccount
          ? `${defaultAccount.id} - ${defaultAccount.name}`
          : undefined
      );
    }
  }, [formDefaultAccount, isFetching]);

  useEffect(() => {
    const isSuperAdmin = formScope === userRoleMapping[UserRole.SUPER_ADMIN];
    if (isSuperAdmin) {
      return;
    }
    if (!formDefaultAccount) {
      return;
    }
    const defaultAccountIsPresent = formAccountIds.find(
      (account: string) => account === formDefaultAccount
    );
    if (!defaultAccountIsPresent) {
      setSelectedDefaultAccount(undefined);
      setValue("defaultAccount", undefined);
    }
  }, [formScope, formAccountIds, formDefaultAccount]);

  const parsedFilteredAccountIds = useMemo(() => {
    return filteredAccountIds.map(
      (account: IAccount) => `${account.id} - ${account.name}`
    );
  }, [filteredAccountIds]);

  const defaultAccountOptions = useMemo(() => {
    const isSuperAdmin = formScope === userRoleMapping[UserRole.SUPER_ADMIN];
    const allOptions = accounts.map(
      (account: IAccount) => `${account.id} - ${account.name}`
    );
    if (isSuperAdmin) {
      return allOptions;
    }
    return selectedAccountIds;
  }, [accounts, formScope, selectedAccountIds]);

  const search = (event: { query: string }) => {
    let filteredAccountIds;

    if (!event.query.trim().length) {
      filteredAccountIds = [...accounts];
    } else {
      filteredAccountIds = accounts.filter((account: IAccount) => {
        return `${account.id} - ${account.name}`
          .toLowerCase()
          .includes(event.query.toLowerCase());
      });
    }

    setFilteredAccountIds(filteredAccountIds);
  };

  return (
    <div className="formgrid grid w-100">
      <div className="field p-fluid col-12">
        <Label htmlFor="code">Account IDs</Label>

        <AutoComplete
          multiple
          dropdown
          id="accountIds"
          className="w-full"
          completeMethod={search}
          value={selectedAccountIds}
          data-testid="autocomplete"
          placeholder="Select Account IDs..."
          suggestions={parsedFilteredAccountIds}
          onChange={(e) => {
            const accountIds = e.value.map(
              (accountId: string) => accountId.split(" ")[0]
            );
            setValue("accountIds", accountIds);
            setSelectedAccountIds(e.value);
            const defaultAccountIsPresent = e.value.find(
              (account: string) => account === selectedDefaultAccount
            );
            const isSuperAdmin =
              formScope === userRoleMapping[UserRole.SUPER_ADMIN];
            if (!defaultAccountIsPresent && !isSuperAdmin) {
              setSelectedDefaultAccount(undefined);
              setValue("defaultAccount", undefined);
            }
          }}
        />
      </div>
      <div className="field p-fluid col-6">
        <Label htmlFor="code">Default Account</Label>
        <Dropdown
          options={defaultAccountOptions}
          value={selectedDefaultAccount}
          onChange={(e) => {
            const defaultAccountId = e.value.split(" ")[0];
            setSelectedDefaultAccount(e.value);
            setValue("defaultAccount", defaultAccountId);
          }}
        />
      </div>
    </div>
  );
}
